import { useState } from "react";

import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { TabContent } from "@app/analytics/account/WorkspaceSettings/components/styles";
import UpsellTooltipWrapper from "@app/analytics/components/UpsellTooltipWrapper";
import useAuth from "@app/common/auth/useAuth";
import { pluralize } from "@app/common/utils";
import {
  EnterpriseWorkspace,
  EnterpriseWorkspaceUser,
} from "@app/common/workspaces/types";
import useWorkspace from "@app/common/workspaces/useWorkspace";
import { isWorkspaceAdmin } from "@app/common/workspaces/workspaceUtils";

import { Container, MainContentContainer, SidePanelContainer } from "./styles";
import TeamsTable from "./TeamsTable";
import MigrateMeetingsConfirmationModal from "../components/MigrateMeetingsConfirmationModal";
import WorkspaceActionButton from "../components/WorkspaceActionButton";
import WorkspaceHeader from "../components/WorkspaceHeader";
import CreateTeamModals from "../modals/CreateTeamModals";
import WorkspaceSidePanel from "../WorkspaceSidePanel";

const getWorkspaceHeaderSubtitle = (
  workspace: EnterpriseWorkspace,
  workspaceUser: EnterpriseWorkspaceUser,
  isAdmin: boolean
) => {
  if (!isAdmin) {
    if (workspaceUser.teams?.length === 0) {
      return "You don't belong to any teams in this Workspace.";
    }

    return `You belong to ${(workspaceUser.teams ?? []).length} ${pluralize(
      "team",
      (workspaceUser.teams ?? []).length
    )} in this Workspace`;
  }

  if (workspace.teams.length === 0) {
    return "No teams in this Workspace";
  }

  return `${workspace.teams.length} ${pluralize(
    "team",
    workspace.teams.length
  )} in this Workspace`;
};

const TeamsHome = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [addTeamOpen, setAddTeamOpen] = useState(false);

  const { workspace, workspaceUser, migrateMeetings, workspaceIsInactive } =
    useWorkspace();
  const { user } = useAuth();
  const [queryParams] = useSearchParams();
  const canMigrateMeetings = queryParams.get("migrateMeetings") === "true";

  const handleCloseMigrateMeetings = () => {
    queryParams.delete("migrateMeetings");
    navigate({ pathname: location.pathname, search: queryParams.toString() });
  };

  const handleMigrateMeetingsConfirm = async () => {
    if (!workspaceUser.userId) return;

    migrateMeetings.mutate(workspaceUser.userId, {
      onSuccess: () => {
        handleCloseMigrateMeetings();
      },
    });
  };

  const showSidePanel = Boolean(user?.accessPermissions?.inviteUsers);
  const canAddTeams = isWorkspaceAdmin(workspaceUser);

  return (
    <>
      <MigrateMeetingsConfirmationModal
        loading={migrateMeetings.isPending}
        open={canMigrateMeetings}
        onClose={handleCloseMigrateMeetings}
        onConfirm={handleMigrateMeetingsConfirm}
      />
      {canAddTeams && (
        <CreateTeamModals
          open={addTeamOpen}
          onClose={() => setAddTeamOpen(false)}
        />
      )}
      <Container spacing={2}>
        <MainContentContainer
          sx={{ width: { lg: showSidePanel ? "70%" : undefined } }}
        >
          <WorkspaceHeader
            title={workspace.name}
            subtitle={getWorkspaceHeaderSubtitle(
              workspace,
              workspaceUser,
              isWorkspaceAdmin(workspaceUser)
            )}
            type="workspace"
            actions={
              canAddTeams ? (
                <UpsellTooltipWrapper show={workspaceIsInactive}>
                  <WorkspaceActionButton
                    disabled={workspaceIsInactive}
                    text="New Team"
                    onClick={() => setAddTeamOpen(true)}
                  />
                </UpsellTooltipWrapper>
              ) : (
                []
              )
            }
          />
          <TabContent sx={{ pb: { xxs: 2, lg: "70px" } }}>
            <TeamsTable setAddTeamOpen={setAddTeamOpen} />
          </TabContent>
        </MainContentContainer>
        {showSidePanel && (
          <SidePanelContainer>
            <WorkspaceSidePanel />
          </SidePanelContainer>
        )}
      </Container>
    </>
  );
};

export default TeamsHome;
